// Local skincare-plan reminders.
//
// LIMITATION: a PWA cannot reliably fire notifications when fully closed without
// a push backend + service worker (deferred). For now these fire only while the
// app is open (setTimeout) plus an immediate confirmation. The interface is
// shaped so a real Web Push scheduler can replace the body later.

export async function requestPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch (e) {
    return 'denied';
  }
}

const timers = [];

export function clearReminders() {
  timers.forEach((t) => clearTimeout(t));
  timers.length = 0;
}

function notify(title, body) {
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      // eslint-disable-next-line no-new
      new Notification(title, { body });
      return true;
    }
  } catch (e) {
    /* ignore */
  }
  return false;
}

// reminders: [{ at: ISOString, title, body }]
export function scheduleReminders(reminders = []) {
  clearReminders();
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const now = Date.now();
  reminders.forEach((r) => {
    const delay = new Date(r.at).getTime() - now;
    // setTimeout max is ~24.8 days; only schedule what we can hold this session.
    if (delay > 0 && delay < 2147483647) {
      timers.push(setTimeout(() => notify(r.title, r.body), delay));
    }
  });
}

// Build a simple daily-routine reminder set for the next `days` days at `hour`.
export function buildPlanReminders(plan, { hour = 9, days = 7 } = {}) {
  const reminders = [];
  const base = new Date();
  for (let d = 1; d <= days; d += 1) {
    const at = new Date(base);
    at.setDate(base.getDate() + d);
    at.setHours(hour, 0, 0, 0);
    reminders.push({
      at: at.toISOString(),
      title: 'Your skincare routine',
      body: 'Time to apply your recommended products for your best glow.',
    });
  }
  return reminders;
}

// Convenience used when the user saves a plan: ask permission, schedule, and
// give an immediate confirmation notification.
export async function activatePlan(plan) {
  const perm = await requestPermission();
  if (perm !== 'granted') return { permission: perm, scheduled: 0 };
  const reminders = buildPlanReminders(plan);
  scheduleReminders(reminders);
  notify('Skincare plan saved', 'We’ll remind you to keep up your routine while the app is open.');
  return { permission: perm, scheduled: reminders.length, reminders };
}
